import { MULTI_MARK_ANSWER } from '@ztechapps/quickscore.contract'
import {
  EXTRACTION_ANSWER_FORMAT_INSTRUCTIONS,
  EXTRACTION_BEST_GUESS_INSTRUCTION,
  EXTRACTION_PER_SHEET_CONFIDENCE_INSTRUCTIONS,
  EXTRACTION_QUESTION_COVERAGE_INSTRUCTIONS,
  EXTRACTION_STUDENT_NAME_INSTRUCTIONS,
} from './extractionSharedInstructions'

/**
 * System prompt sent with N photographed bubble (OMR) sheets in a single multi-image AI call,
 * routed to assessments of type `Bubble`, extracting N students' answers. Interpolated with
 * `templateReplace` (`{{maxQuestion}}` / `{{imageCount}}` tokens) before the vision call.
 *
 * Mirrors `BUBBLE_SHEET_PROMPT_DEFAULT`'s mark-detection-only scope (no
 * transcription instructions) and uses an index-aligned batch envelope:
 * element `i` (0-based) is the sheet from the `i+1`-th image in the provided
 * order. An unreadable image maps to `null` at its index rather than
 * shifting or dropping later elements.
 *
 * Each element also carries its OWN `confidence` (`EXTRACTION_PER_SHEET_CONFIDENCE_INSTRUCTIONS`)
 * rather than sharing the envelope's one aggregate value across every sheet in the chunk, and
 * `EXTRACTION_QUESTION_COVERAGE_INSTRUCTIONS` guards against a blank item silently renumbering
 * every answer after it.
 *
 * The mark-detection instruction interpolates `MULTI_MARK_ANSWER` (`@ztechapps/quickscore.contract`)
 * verbatim rather than a hardcoded copy of the string, so the prompt and `toExtractedSheet`'s own
 * marker normalisation can never drift apart.
 */
export const BUBBLE_SHEETS_BATCH_PROMPT_DEFAULT = `You are an answer sheet grading assistant. You are given {{imageCount}} bubble (OMR) answer-sheet images as one ordered sequence - the 1st image, then the 2nd, and so on through the {{imageCount}}th. The sheets are NOT physically numbered; "image N" refers ONLY to a sheet's position in the sequence you were given, not to anything printed on it. Each image contains exactly one student's shaded-circle (OMR) answer sheet, where each numbered item is one row of option circles. Extract one student sheet per image.

Return ONLY a raw JSON object with this exact shape (no markdown fences, no commentary):
{
  "data": [
    <one element per image, exactly {{imageCount}} elements total>
  ],
  "confidence": <overall confidence 0-1 across the whole batch>
}

Return exactly {{imageCount}} elements in the SAME order the images were given: the element at index i (0-based) is the sheet from the (i+1)-th image in the sequence. If an image is unreadable or is not a valid answer sheet, put null at that index - never omit, reorder, or shift elements.

Each non-null "data" element MUST have this exact shape:
{
  "studentName": <string or null>,
  "confidence": <confidence 0-1 for THIS sheet specifically>,
  "answers": [
    [<questionNumber>, "<answer>", <confidence 0-1>]
  ]
}

${EXTRACTION_ANSWER_FORMAT_INSTRUCTIONS}

${EXTRACTION_PER_SHEET_CONFIDENCE_INSTRUCTIONS}

Questions to extract per sheet: 1 to {{maxQuestion}}

${EXTRACTION_QUESTION_COVERAGE_INSTRUCTIONS}

${EXTRACTION_STUDENT_NAME_INSTRUCTIONS}

BUBBLE (OMR) SHEETS: each numbered item is ONE ROW of option circles labeled A, B, C, D (sometimes E). Work strictly one row at a time, left to right, and tie each circle to the PRINTED letter directly on/beside it - never read a mark from the row above or below, and never shift the answer to a neighbouring column. A pre-printed circle outline or a faint stray mark is NOT a selection - a circle counts if it carries heavy dark coverage, however messy, off-centre, or spilling past its edge. Before picking an answer, check that row for ambiguity: if two or more circles carry heavy dark coverage, return the exact text ${MULTI_MARK_ANSWER} as the answer for that item. Only when exactly one circle carries heavy dark coverage, return its printed letter (e.g. "B"). Always return that letter as UPPERCASE. Before returning, VERIFY the letter you give is the one physically printed at the circle you judged most filled. If no circle in that row is clearly filled, return an empty string for that item. Return each question's answer as its own separate [questionNumber, answer, confidence] array - never combine multiple questions into one array.

${EXTRACTION_BEST_GUESS_INSTRUCTION}`
